import checkIcon from "@tabler/icons/outline/check.svg";
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { cn } from "@/utils/cn";

import { Counter, Icon, Text } from "../ui";

import DropdownMenuList from "./dropdown-menu-list";

export interface MenuItem {
  action?: React.EventHandler<React.KeyboardEvent | React.MouseEvent>;
  active?: boolean;
  count?: number;
  destructive?: boolean;
  disabled?: boolean;
  href?: string;
  icon?: string;
  meta?: string;
  middleClick?(event: React.MouseEvent): void;
  paragraph?: boolean;
  target?: React.HTMLAttributeAnchorTarget;
  text: string | React.ReactNode;
  to?: string;
  toggle?: React.ReactNode;
  testId?: string;
  items?: { text: string; isSelected: boolean; onSelect: () => void }[];
}

interface DropdownMenuItemProps {
  index: number;
  item: MenuItem | null;
  onClick?(): void;
  handleLayout(): void;
}

const DropdownMenuItem = ({
  index,
  item,
  onClick,
  handleLayout,
}: DropdownMenuItemProps) => {
  const navigate = useNavigate();

  const itemRef = useRef<HTMLAnchorElement>(null);

  const classes = cn({
    "truncate focus-visible:ring-2 focus-visible:ring-primary-500":
      !item?.paragraph,
  });

  const handleClick: React.EventHandler<
    React.MouseEvent | React.KeyboardEvent
  > = (event) => {
    event.stopPropagation();

    if (!item) return;
    if (item.disabled) return;
    if (onClick) onClick();

    if (item.to) {
      event.preventDefault();
      navigate(item.to);
    } else if (typeof item.action === "function") {
      event.preventDefault();
      item.action(event);
    }
  };

  const handleAuxClick: React.EventHandler<React.MouseEvent> = (event) => {
    if (!item) return;
    if (onClick) onClick();

    if (event.button === 1 && item.middleClick) {
      item.middleClick(event);
    }
  };

  const handleItemKeyPress: React.EventHandler<React.KeyboardEvent> = (
    event,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      handleClick(event);
    }
  };

  useEffect(() => {
    const firstItem = index === 0;

    if (itemRef.current && firstItem) {
      itemRef.current.focus({ preventScroll: true });
    }
  }, [itemRef.current, index]);

  if (item === null) {
    return <li className="mx-4 my-1 h-px bg-gray-300 dark:bg-gray-800" />;
  }

  if (item.text && item.toggle) {
    return (
      <li
        className={cn(
          classes,
          "flex items-center justify-between gap-x-4 px-3 py-1",
        )}
      >
        <Text size="sm" weight="medium" className="p-3" theme="muted">
          {item.text}
        </Text>

        {item.toggle}
      </li>
    );
  }

  return (
    <li className={classes}>
      {item.paragraph && !item.action && (
        <a
          href={item.href || item.to || "#"}
          role="button"
          tabIndex={0}
          ref={itemRef}
          data-index={index}
          onClick={(event) => event.preventDefault()}
          className="cursor-default"
        >
          {typeof item.text === "string" ? (
            <Text size="sm" weight="medium" className="p-3">
              {item.text}
            </Text>
          ) : (
            item.text
          )}
        </a>
      )}

      {(item.action || item.to) && (
        <a
          href={item.href || item.to || "#"}
          role="button"
          tabIndex={0}
          ref={itemRef}
          data-index={index}
          onClick={handleClick}
          onAuxClick={handleAuxClick}
          onKeyPress={handleItemKeyPress}
          target={item.target}
          title={typeof item.text === "string" ? item.text : undefined}
          data-testid={item.testId}
          aria-disabled={item.disabled}
          className={cn(
            "flex cursor-pointer rounded-md px-4 py-2.5 text-sm text-gray-900 hover:bg-gray-100 focus:outline-hidden dark:text-gray-100 dark:hover:bg-gray-800",
            {
              "text-danger-600! hover:bg-danger-50 dark:text-danger-400! dark:hover:bg-danger-800/20":
                item.destructive,
              "pointer-events-none cursor-not-allowed opacity-25":
                item.disabled,
            },
          )}
        >
          {item.icon && (
            <Icon
              src={item.icon}
              className={cn(
                "mr-3 size-5 flex-none text-gray-900 rtl:mr-0 rtl:ml-3 dark:text-white",
                {
                  "text-danger-600! dark:text-danger-400!": item.destructive,
                },
              )}
            />
          )}

          {typeof item.text === "string" ? (
            <span
              className={cn(
                "truncate font-medium text-gray-900 dark:text-gray-100",
                {
                  "text-danger-600! dark:text-danger-400!": item.destructive,
                },
              )}
            >
              {item.text}
            </span>
          ) : (
            item.text
          )}

          {item.active && (
            <div className="ml-auto">
              <Icon
                src={checkIcon}
                className="size-5 text-gray-900 dark:text-white"
              />
            </div>
          )}

          {item.count ? (
            <span className="ml-auto size-5 flex-none">
              <Counter count={item.count} />
            </span>
          ) : null}
        </a>
      )}

      {item.items && (
        <DropdownMenuList
          item={item}
          onToggle={handleLayout}
          handleClose={() => (onClick ? onClick() : null)}
        />
      )}
    </li>
  );
};

export default DropdownMenuItem;
